/**
 * Document utility functions for processing markdown and text documents
 */

export interface DocumentMetadata {
  wordCount: number;
  readingTime: number; // in minutes
  title: string;
  headingCount: number;
  listCount: number;
  hasCodeBlocks: boolean;
  estimatedSize: string;
}

/**
 * Count words in text content
 */
export function countWords(content: string): number {
  if (!content || typeof content !== "string") return 0;

  // Remove markdown formatting for accurate word count
  const cleanText = content
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, "")
    // Remove inline code
    .replace(/`[^`]*`/g, "")
    // Remove markdown links but keep text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Remove markdown formatting
    .replace(/[*_~#>\-+]/g, "")
    // Remove extra whitespace
    .replace(/\s+/g, " ")
    .trim();

  if (!cleanText) return 0;

  // Split by whitespace and filter empty strings
  const words = cleanText.split(/\s+/).filter((word) => word.length > 0);

  return words.length;
}

/**
 * Estimate reading time in minutes (average 200 words per minute)
 */
export function estimateReadingTime(wordCount: number): number {
  const wordsPerMinute = 200;
  const minutes = Math.ceil(wordCount / wordsPerMinute);

  return Math.max(1, minutes); // Minimum 1 minute
}

/**
 * Extract document title from content
 */
export function extractDocumentTitle(content: string, fallbackTitle?: string): string {
  if (!content) return fallbackTitle || "Untitled Document";

  // Look for first heading (# Title)
  const headingMatch = content.match(/^#\s+(.+)$/m);

  if (headingMatch) {
    return headingMatch[1].trim();
  }

  // Look for title in first few lines
  const lines = content.split("\n").slice(0, 5);

  for (const line of lines) {
    const trimmed = line.trim();

    if (trimmed && !trimmed.startsWith("#") && !trimmed.startsWith("```")) {
      // Use first non-empty, non-markdown line as title
      const cleanTitle = trimmed
        .replace(/[*_~`]/g, "")
        .substring(0, 50)
        .trim();

      if (cleanTitle) {
        return cleanTitle;
      }
    }
  }

  return fallbackTitle || "Untitled Document";
}

/**
 * Count markdown headings
 */
export function countHeadings(content: string): number {
  if (!content) return 0;

  const headingMatches = content.match(/^#{1,6}\s+.+$/gm);

  return headingMatches ? headingMatches.length : 0;
}

/**
 * Count markdown lists (both ordered and unordered)
 */
export function countLists(content: string): number {
  if (!content) return 0;

  const listMatches = content.match(/^[\s]*[-*+]\s|^[\s]*\d+\.\s/gm);

  return listMatches ? listMatches.length : 0;
}

/**
 * Check if content has code blocks
 */
export function hasCodeBlocks(content: string): boolean {
  if (!content) return false;

  return /```[\s\S]*?```|`[^`]+`/.test(content);
}

/**
 * Format file size estimate
 */
export function formatFileSize(content: string): string {
  const bytes = new Blob([content]).size;

  if (bytes < 1024) {
    return `${bytes} bytes`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Generate comprehensive document metadata
 */
export function generateDocumentMetadata(
  content: string,
  fallbackTitle?: string,
): DocumentMetadata {
  const wordCount = countWords(content);
  const readingTime = estimateReadingTime(wordCount);
  const title = extractDocumentTitle(content, fallbackTitle);
  const headingCount = countHeadings(content);
  const listCount = countLists(content);
  const hasCode = hasCodeBlocks(content);
  const estimatedSize = formatFileSize(content);

  return {
    wordCount,
    readingTime,
    title,
    headingCount,
    listCount,
    hasCodeBlocks: hasCode,
    estimatedSize,
  };
}

/**
 * Generate document preview (first few lines)
 */
export function generateDocumentPreview(content: string, maxLines: number = 3): string {
  if (!content) return "Empty document";

  const lines = content
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, maxLines);

  if (lines.length === 0) return "Empty document";

  // Clean markdown formatting for preview
  const preview = lines
    .map((line) => {
      return line
        .replace(/^#+\s*/, "") // Remove heading markers
        .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold
        .replace(/\*(.*?)\*/g, "$1") // Remove italic
        .replace(/`(.*?)`/g, "$1") // Remove inline code
        .substring(0, 80); // Limit line length
    })
    .join(" • ");

  return preview.length > 200 ? preview.substring(0, 197) + "..." : preview;
}

/**
 * Format reading time for display
 */
export function formatReadingTime(minutes: number): string {
  if (minutes === 1) return "1 min read";
  if (minutes < 60) return `${minutes} min read`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return hours === 1 ? "1 hour read" : `${hours} hours read`;
  }

  return `${hours}h ${remainingMinutes}m read`;
}

/**
 * Format word count for display
 */
export function formatWordCount(count: number): string {
  if (count < 1000) return `${count} words`;
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K words`;

  return `${(count / 1000000).toFixed(1)}M words`;
}

/**
 * Sanitize markdown syntax from text to create clean, readable content.
 * Removes all markdown formatting while preserving the actual text content.
 *
 * @param text - The markdown text to sanitize
 * @returns Clean text with markdown syntax removed
 *
 * @example
 * sanitizeMarkdown("# Title\n**Bold** text") // "Title Bold text"
 * sanitizeMarkdown("[Image #1] Description") // "Image 1 Description"
 */
export function sanitizeMarkdown(text: string): string {
  if (!text) return "";

  const transformations = [
    // 1. Remove complete code blocks with content (```lang\ncontent\n```)
    { pattern: /```[a-zA-Z]*\n[\s\S]*?\n```/g, replacement: "" },

    // 2. Remove heading markers (### Header -> Header)
    { pattern: /^#{1,6}\s+/gm, replacement: "" },

    // 3. Remove images and preserve alt text (![alt](url) -> alt)
    { pattern: /!\[([^\]]*)\]\([^)]+\)/g, replacement: "$1" },

    // 4. Remove links and preserve text ([text](url) -> text)
    { pattern: /\[([^\]]+)\]\([^)]+\)/g, replacement: "$1" },

    // 5. Handle special image pattern ([Image #1] -> Image 1)
    { pattern: /\[Image #(\d+)\]/g, replacement: "Image $1" },

    // 6. Remove emphasis formatting
    { pattern: /\*\*([^*]+)\*\*/g, replacement: "$1" }, // **bold**
    { pattern: /\*([^*]+)\*/g, replacement: "$1" }, // *italic*
    { pattern: /~~([^~]+)~~/g, replacement: "$1" }, // ~~strikethrough~~

    // 7. Handle code block markers (complex patterns first)
    { pattern: /```(\w+)\s+```\s+(.+)/g, replacement: "$1 $2" }, // ```html ``` content
    { pattern: /```(\w+)\s+(.+)/g, replacement: "$1 $2" }, // ```html content
    { pattern: /```(\w+)$/gm, replacement: "$1" }, // ```python
    { pattern: /```/g, replacement: "" }, // remaining ```

    // 8. Remove inline code formatting
    { pattern: /`([^`]+)`/g, replacement: "$1" }, // `code`
    { pattern: /`+/g, replacement: "" }, // remaining backticks

    // 9. Remove blockquotes
    { pattern: /^>\s*/gm, replacement: "" }, // > at start of lines
    { pattern: /\s>\s*/g, replacement: " " }, // > within content

    // 10. Normalize whitespace
    { pattern: /\s+/g, replacement: " " },
  ];

  return transformations
    .reduce((result, { pattern, replacement }) => result.replace(pattern, replacement), text)
    .trim();
}
